import React, { Suspense, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { OverlayPanel } from "primereact/overlaypanel";
import { T } from "services/lang/LanguageService";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { Button } from "primereact/button";
import useDashboardStore from "store/dashboard-store";
import { TabPanel, TabView } from "primereact/tabview";
import useTabId from "hooks/dashboard/useTabId";
import Editor from "@monaco-editor/react";
import { InputSwitch } from "primereact/inputswitch";
import { v4 as uuidv4 } from "uuid";
import { ComponentTypeMappings, dashboardComponentOptions } from "components/component-mappings";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { MissingComponentWarning } from "core/MissingComponentWarning";
import { ProgressSpinner } from "primereact/progressspinner";
import { DashboardLayoutType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import { textAligns, textSizes } from "utils/constants";
import { getDefaultLayout } from "../layouts/helpers";

export const defaultComponent = (layoutType: DashboardLayoutType): IDashboardComponent => ({
  id: uuidv4(),
  title: T("word.new-component"),
  titleSize: "2xl",
  titleAlign: "left",
  type: "",
  collapsable: false,
  defaultCollapsed: false,
  hideHeader: false,
  config: {},
  layout: getDefaultLayout(layoutType),
});

export enum ConfiguratorModes {
  EDIT = "edit",
  ADD = "add",
}

const schemasLocation = `${window.jsBasePath}/assets/generated/schemas/dashboard-components/`;

interface DashboardComponentConfiguratorNewProps {
  isDialogVisible: boolean;
  setIsDialogVisible: (isDialogVisible: boolean) => void;
  component: IDashboardComponent;
  onComponentChange?: (config: IDashboardComponent) => void;
  mode: ConfiguratorModes;
  layoutType: DashboardLayoutType;
  groupId?: string;
}

const DashboardComponentConfiguratorNew = (props: DashboardComponentConfiguratorNewProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const tabId = useTabId();
  const [currentConfig, setCurrentConfig] = useState<IDashboardComponent>(
    props.mode === ConfiguratorModes.ADD ? defaultComponent(props.layoutType) : props.component
  );
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [tooltipImage, setTooltipImage] = useState<string | null>(null);
  const [jsonSchema, setJsonSchema] = useState<any>(null);

  const overlayRef = useRef<OverlayPanel>(null);
  const wasTypeClickedRef = useRef<boolean>(false);

  let EditorComponent;

  if (currentConfig?.type) {
    EditorComponent = ComponentTypeMappings[currentConfig.type].editorEntryPoint;
  }

  const loadSchema = async () => {
    if (currentConfig?.type) {
      try {
        const schema = await fetch(`${schemasLocation}${currentConfig?.type}.json`).then(response =>
          response.json()
        );
        setJsonSchema(schema);
      } catch {
        easasAlert({ summary: T("alert.failed-to-load-schema"), severity: "error" });
      }
    }
  };

  useEffect(() => {
    loadSchema();
  }, [currentConfig?.type]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement> | DropdownChangeEvent) => {
    setCurrentConfig((prevState: IDashboardComponent) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  };
  const handleWidthChange = (e: number) => {
    setCurrentConfig((prevState: IDashboardComponent) => ({
      ...prevState,
      layout: {
        ...prevState.layout,
        [props.layoutType]: {
          ...prevState.layout[props.layoutType],
          width: e,
        },
      },
    }));
  };

  const handleTypeChange = (e: DropdownChangeEvent | { value: string }) => {
    wasTypeClickedRef.current = true;
    handleUnselectTooltipImage();

    setCurrentConfig((prevState: IDashboardComponent) => ({
      ...prevState,
      config: {},
      type: e.value,
    }));
  };

  const handleEditorChange = (config: Partial<IDashboardComponent>) => {
    setCurrentConfig(prevState => ({
      ...prevState,
      config,
    }));
  };

  const handleCancelAddingComponent = () => {
    props.setIsDialogVisible(false);
    setCurrentConfig(defaultComponent(props.layoutType));
  };

  const handleAddComponentToCurrentTab = () => {
    const newComponent = {
      ...currentConfig,
      id: uuidv4(),
      layout: currentConfig.layout,
    };

    const newDashboard = {
      ...dashboard,
      dashboardTabsJs: dashboard?.dashboardTabsJs?.map((tab, index) => {
        if (index === tabId) {
          return {
            ...tab,
            componentGroups: tab.componentGroups.map(group =>
              group.id === props.groupId
                ? {
                    ...group,
                    components: [...(group.components || []), newComponent],
                  }
                : group
            ),
          };
        }
        return tab;
      }),
    } as Dashboard;
    setDashboard(newDashboard);
    props.setIsDialogVisible(false);
    setCurrentConfig(defaultComponent(props.layoutType));
  };

  const handleUpdateComponent = () => {
    const updatedComponent = {
      ...currentConfig,
      id: currentConfig.id || uuidv4(),
      layout: currentConfig.layout,
    };
    props.onComponentChange?.(updatedComponent);
    props.setIsDialogVisible(false);
    setCurrentConfig(defaultComponent(props.layoutType));
  };

  const handleMouseEnterTooltipImage = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    tooltipImg: string
  ) => {
    if (wasTypeClickedRef.current) return;
    setTooltipImage(tooltipImg);
    overlayRef.current?.show(e, e.target);
  };

  const handleMouseLeaveTooltipImage = () => {
    handleUnselectTooltipImage();
    wasTypeClickedRef.current = false;
  };

  const handleUnselectTooltipImage = () => {
    overlayRef.current?.hide();
    setTooltipImage(null);
  };

  const componentTypeItemTemplate = (option: {
    id: string;
    name: string;
    tooltipImage: string;
  }) => {
    return (
      <div className="flex flex-row justify-content-between w-full">
        <div className="my-auto">{option.name}</div>
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-text p-button-plain p-0 h-1rem"
          onClick={() => handleTypeChange({ value: option.id })}
          onMouseEnter={e => handleMouseEnterTooltipImage(e, option.tooltipImage)}
          onMouseLeave={handleMouseLeaveTooltipImage}
        />
      </div>
    );
  };

  return (
    <Dialog
      visible={props.isDialogVisible}
      onHide={() => props.setIsDialogVisible(false)}
      header={
        props.mode === ConfiguratorModes.ADD
          ? T("action.add-component")
          : T("action.edit-component")
      }
      footer={
        <div>
          <Button
            label={T("word.cancel")}
            icon="pi pi-times"
            onClick={handleCancelAddingComponent}
            className="component-editor-button icon-red"
          />
          <Button
            label={props.mode === ConfiguratorModes.ADD ? T("action.add") : T("action.save")}
            icon="pi pi-plus"
            onClick={
              props.mode === ConfiguratorModes.ADD
                ? handleAddComponentToCurrentTab
                : handleUpdateComponent
            }
            className="component-editor-button icon-green"
          />
        </div>
      }
      style={{ width: "70rem" }}
    >
      <div className="py-0 px-3">
        <OverlayPanel ref={overlayRef} className="overlaypanel-content-background">
          {tooltipImage ? (
            <img src={tooltipImage} alt="Component preview" className="w-30rem border-round-lg" />
          ) : (
            <div>{T("message.no-preview-available")}</div>
          )}
        </OverlayPanel>
        <div className="p-fluid flex flex-row justify-content-between">
          <div className="p-col w-6">
            <label htmlFor="title">{T("word.title")}</label>
            <InputText
              id="title"
              type="text"
              value={currentConfig?.title ?? T("word.new-component")}
              onChange={handleTitleChange}
            />
          </div>
          <div className="p-col w-6">
            <label htmlFor="type">{T("label.component-type")}</label>
            <Dropdown
              value={currentConfig?.type}
              options={dashboardComponentOptions}
              filter
              onChange={handleTypeChange}
              optionLabel="name"
              optionValue="id"
              placeholder={T("action.select-a-component-type")}
              itemTemplate={componentTypeItemTemplate}
            />
          </div>
        </div>
        <div className="flex flex-row flex-wrap justify-content-start">
          <div className="p-3 flex flex-column w-3">
            <label htmlFor="width">{T("word.width")}</label>
            <InputNumber
              inputId="width"
              min={2}
              max={12}
              value={currentConfig?.layout[props.layoutType]?.width ?? 6}
              onValueChange={e => handleWidthChange(e.value ?? 6)}
            />
          </div>
          <div className="p-3 flex flex-column w-12rem">
            <label htmlFor="titleSize">{T("word.title-size")}</label>
            <Dropdown
              id="titleSize"
              value={currentConfig?.titleSize ?? "2xl"}
              options={textSizes}
              onChange={handleTitleChange}
            />
          </div>
          <div className="p-3 flex flex-column w-14rem">
            <label htmlFor="titleAlign">{T("word.title-alignment")}</label>
            <Dropdown
              id="titleAlign"
              value={currentConfig?.titleAlign ?? "left"}
              options={textAligns}
              onChange={handleTitleChange}
            />
          </div>
          <div className="p-3 flex flex-column w-3 gap-2">
            <label htmlFor="hideHeader">{T("action.hide-component-header")}</label>
            <InputSwitch
              inputId="hideHeader"
              checked={currentConfig?.hideHeader ?? false}
              onChange={e =>
                setCurrentConfig((prevState: IDashboardComponent) => ({
                  ...prevState,
                  hideHeader: e.value,
                }))
              }
            />
          </div>
          <div className="p-3 flex flex-column w-3 gap-2">
            <label htmlFor="height">{T("label.collapsable")}</label>
            <InputSwitch
              inputId="collapsable"
              checked={currentConfig?.collapsable ?? false}
              onChange={e => {
                if (!e.value) {
                  setCurrentConfig((prevState: IDashboardComponent) => ({
                    ...prevState,
                    collapsable: e.value,
                    defaultCollapsed: e.value,
                  }));
                }
                setCurrentConfig((prevState: IDashboardComponent) => ({
                  ...prevState,
                  collapsable: e.value,
                }));
              }}
            />
          </div>
          {currentConfig?.collapsable && (
            <div className="p-3 flex flex-column w-3 gap-2">
              <label htmlFor="height">{T("label.collapsed")}</label>
              <InputSwitch
                inputId="collapsed"
                checked={currentConfig?.defaultCollapsed ?? false}
                onChange={e =>
                  setCurrentConfig((prevState: IDashboardComponent) => ({
                    ...prevState,
                    defaultCollapsed: e.value,
                  }))
                }
              />
            </div>
          )}
        </div>
        <TabView activeIndex={activeTabIndex} onTabChange={e => setActiveTabIndex(e.index)}>
          {currentConfig && currentConfig.type && (
            <TabPanel header={T("label.editor")}>
              <Suspense fallback={<ProgressSpinner />}>
                {EditorComponent ? (
                  <EditorComponent
                    config={currentConfig.config}
                    onConfigChange={handleEditorChange}
                  />
                ) : (
                  <MissingComponentWarning componentId={currentConfig.type as string} />
                )}
              </Suspense>
            </TabPanel>
          )}
          <TabPanel header={T("label.edit-raw-json")}>
            <div className="p-fluid p-formgrid p-grid">
              <div className="p-field p-col">
                <Editor
                  height="420px"
                  defaultLanguage="json"
                  value={JSON.stringify(currentConfig?.config, null, 2)}
                  onChange={value => {
                    try {
                      const parsedValue = JSON.parse(value || "{}");
                      setCurrentConfig(prevState => ({
                        ...prevState,
                        config: parsedValue,
                      }));
                    } catch (error) {
                      // ignore errors while typing
                    }
                  }}
                  onValidate={markers => {
                    if (markers.length > 0) {
                      easasAlert({
                        summary: T("alert.json-is-not-valid"),
                        detail: markers[0].message,
                        severity: "error",
                      });
                    }
                  }}
                  onMount={(editorInstance, monaco) => {
                    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                      validate: true,
                      schemas: [
                        {
                          uri: `${schemasLocation}${currentConfig?.type}`,
                          fileMatch: ["*"],
                          schema: jsonSchema,
                        },
                      ],
                    });
                  }}
                  theme="vs-dark"
                />
              </div>
            </div>
          </TabPanel>
        </TabView>
      </div>
    </Dialog>
  );
};

export default DashboardComponentConfiguratorNew;
